/* NutriChef PDP enhancements (product template only).
   - Price per day / per meal and price hints on option buttons
   - Seal Subscriptions widget: savings badge and per-option prices
   - Sticky add-to-cart bar that clicks the real form button, so the theme
     cart drawer and the Seal subscription choice both keep working. */
(() => {
  const info = document.querySelector('product-info[id^="MainProduct"]') || document.querySelector('product-info');
  if (!info) return;

  const form = info.querySelector('form[data-product-form]') || info.querySelector('form[action*="/cart/add"]');
  const valueEl = info.querySelector('[data-nc-value]');
  let variants = [];
  try {
    variants = JSON.parse(valueEl?.querySelector('[data-nc-variants]')?.textContent || '[]');
  } catch (e) {
    variants = [];
  }

  const money = (cents) => {
    const fmt = window.theme?.moneyFormat || '{{amount}}';
    const out = window.theme?.formatMoney ? window.theme.formatMoney(Math.round(cents), fmt) : (cents / 100).toFixed(2);
    return out.replace(/[.,]00(?=\D*$)/, '');
  };

  // Whole currency units for derived figures (per day, per meal, hints).
  const moneyRound = (cents) => money(Math.round(cents / 100) * 100);

  const idInput = () => form?.querySelector('input[name="id"]');
  const currentVariant = () => {
    const id = Number(idInput()?.value);
    return variants.find((v) => v.id === id) || null;
  };

  /* ---------- Per day / per meal ---------- */
  const daysIndex = Number(valueEl?.dataset.daysIndex ?? -1);
  const mealsIndex = Number(valueEl?.dataset.mealsIndex ?? -1);
  const daysOf = (v) => (daysIndex > -1 ? parseInt(v.options[daysIndex], 10) || 0 : 0);
  const mealsOf = (v) => {
    if (mealsIndex < 0) return 0;
    const m = String(v.options[mealsIndex]).match(/breakfast|lunch|dinner|snack/gi);
    return m ? m.length : 0;
  };

  const updateValue = (v) => {
    if (!valueEl || !v) return;
    const days = daysOf(v);
    const meals = mealsOf(v);
    const perDay = valueEl.querySelector('[data-nc-per-day]');
    const perMeal = valueEl.querySelector('[data-nc-per-meal]');
    if (days > 0) {
      perDay.querySelector('strong').textContent = moneyRound(v.price / days);
      perDay.hidden = false;
    } else perDay.hidden = true;
    if (days > 0 && meals > 0) {
      perMeal.querySelector('strong').textContent = moneyRound(v.price / days / meals);
      perMeal.hidden = false;
    } else perMeal.hidden = true;
    valueEl.hidden = perDay.hidden && perMeal.hidden;
  };

  /* ---------- "Build your plan" configurator ----------
     The theme re-renders variant-selects on every change, so the option
     labels are rebuilt each time from the input values and variant map. */
  const findVariant = (options) => variants.find((v) => v.options.every((o, i) => o === options[i]));
  const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  const MEAL_ICONS = {
    breakfast: '<path d="M4 12h12M6 12a4 4 0 0 1 8 0M10 5v2M4.6 7.4l1.4 1.4M15.4 7.4 14 8.8" />',
    lunch: '<path d="M3.5 9.5h13a6.5 6.5 0 0 1-13 0zM7 6.5c0-1 1-1 1-2M10.5 6.5c0-1 1-1 1-2" />',
    dinner: '<path d="M14.5 12.8A6 6 0 0 1 8 4.2a6 6 0 1 0 6.5 8.6z" />',
    snack: '<path d="M10 7.5c-3-2-6 0-5.5 3.5S7.5 17 10 15.8c2.5 1.2 6-1.3 5.5-4.8S13 5.5 10 7.5zM10 7.5c0-2 .8-3.2 2.2-3.8" />',
  };
  const mealIcon = (m) => `<span class="nc-o__icon" title="${m}"><svg viewBox="0 0 20 20" aria-hidden="true">${MEAL_ICONS[m]}</svg></span>`;
  const mealsIn = (value) => (String(value).match(/breakfast|lunch|dinner|snack/gi) || []).map((m) => m.toLowerCase());
  const prettyMeals = (list) => {
    const words = list.map((w, i) => (i === 0 ? w[0].toUpperCase() + w.slice(1) : w));
    return words.length > 1 ? `${words.slice(0, -1).join(', ')} & ${words[words.length - 1]}` : words[0] || '';
  };
  const STEP_NAMES = { size: 'Portion size', meals: 'Meals per day', days: 'Plan length' };
  const SIZE_NOTES = [
    [/small|regular|standard|light/i, 'Lighter portions'],
    [/large|big|xl/i, 'Larger portions'],
  ];

  let stepCount = 0;
  const decorateOptions = (v, root = info.querySelector('variant-selects')) => {
    if (!v || !root || valueEl?.dataset.optionHints !== 'true') return;
    const holder = info.querySelector('.product__form__holder');
    if (!holder) return;
    holder.classList.add('nc-config');
    if (!holder.querySelector('.nc-o__heading') && valueEl.dataset.heading) {
      const h = document.createElement('p');
      h.className = 'nc-o__heading';
      h.textContent = valueEl.dataset.heading;
      holder.prepend(h);
    }

    stepCount = 0;
    root.querySelectorAll('[data-option-position]').forEach((wrapper) => {
      const index = Number(wrapper.dataset.optionPosition) - 1;
      const inputs = [...wrapper.querySelectorAll('input[type="radio"]')];
      if (!inputs.length) return;
      stepCount += 1;

      const nameEl = wrapper.querySelector('.radio__legend__option-name');
      const rawName = nameEl ? nameEl.dataset.ncRaw || nameEl.textContent.trim() : '';
      let kind = 'plain';
      if (index === daysIndex) kind = 'days';
      else if (index === mealsIndex || /meal/i.test(rawName)) kind = 'meals';
      else if (/size|portion/i.test(rawName)) kind = 'size';

      wrapper.classList.add('nc-o', `nc-o--${kind}`);
      const legend = wrapper.querySelector('.radio__legend');
      if (legend && !legend.querySelector('.nc-o__step')) {
        const step = document.createElement('span');
        step.className = 'nc-o__step';
        step.textContent = stepCount;
        legend.prepend(step);
      }
      if (nameEl) {
        nameEl.dataset.ncRaw = rawName;
        if (STEP_NAMES[kind] && /^(size|meals?|days?)$/i.test(rawName)) nameEl.textContent = STEP_NAMES[kind];
      }

      const rows = inputs.map((input) => {
        const options = [...v.options];
        options[index] = input.value;
        const match = findVariant(options);
        const days = match ? daysOf(match) : 0;
        return { input, match, perDay: kind === 'days' && days ? match.price / days : null };
      });
      const prices = rows.filter((r) => r.match).map((r) => r.match.price);
      const min = prices.length ? Math.min(...prices) : 0;
      const perDays = rows.map((r) => r.perDay).filter(Boolean);
      const best = perDays.length > 1 && Math.min(...perDays) < Math.max(...perDays) ? Math.min(...perDays) : null;

      rows.forEach(({ input, match, perDay }) => {
        const label = wrapper.querySelector(`label[for="${input.id}"]`);
        if (!label) return;
        const srOnly = label.querySelector('.label-unavailable');
        const value = input.value;
        const delta = match && match.price > min ? `+${money(match.price - min)}` : '';
        let html = '';

        if (kind === 'days' && parseInt(value, 10)) {
          const n = parseInt(value, 10);
          const unit = value.replace(/^\s*\d+\s*/, '') || 'days';
          html = `<span class="nc-o__num">${n}</span><span class="nc-o__unit">${esc(unit)}</span><span class="nc-o__rule"></span>` +
            (match && perDay ? `<span class="nc-o__per">${moneyRound(perDay)}<small>/day</small></span><span class="nc-o__total">${money(match.price)} total</span>` : '');
        } else if (kind === 'meals') {
          const list = mealsIn(value);
          const title = list.length ? prettyMeals(list) : value;
          html = `<span class="nc-o__icons">${list.map(mealIcon).join('')}</span>` +
            `<span class="nc-o__body"><span class="nc-o__title option-title">${esc(title)}</span>` +
            (list.length ? `<span class="nc-o__sub">${list.length} meals a day</span>` : '') +
            `</span><span class="nc-o__aside">${delta}</span><span class="nc-o__check" aria-hidden="true"></span>`;
        } else if (kind === 'size') {
          const note = (SIZE_NOTES.find(([re]) => re.test(value)) || [])[1] || '';
          html = `<span class="nc-o__title option-title">${esc(value)}</span>` +
            (note || delta ? `<span class="nc-o__sub">${note ? `<span class="nc-o__note">${note}</span>` : ''}${delta ? `<span class="nc-o__delta">${delta}</span>` : ''}</span>` : '');
        } else {
          html = `<span class="nc-o__body"><span class="nc-o__title option-title">${esc(value)}</span></span><span class="nc-o__aside">${delta}</span><span class="nc-o__check" aria-hidden="true"></span>`;
        }

        label.innerHTML = html;
        if (srOnly) label.appendChild(srOnly);
        label.classList.add('nc-o__opt');
        label.classList.toggle('nc-o__opt--na', !match || !match.available);
        if (best && perDay === best) {
          const badge = document.createElement('span');
          badge.className = 'nc-opt-badge';
          badge.textContent = valueEl.dataset.bestLabel || 'Best value';
          label.appendChild(badge);
        }
      });
    });
  };

  /* ---------- Seal Subscriptions ---------- */
  const sealPercent = () => {
    const text = form?.querySelector('.sls-discount-description-container')?.textContent || '';
    const m = text.match(/(\d+(?:\.\d+)?)\s*%/);
    return m ? parseFloat(m[1]) : 0;
  };
  const isSubscription = () => form?.querySelector('.sls-option:checked')?.value === 'subscription';

  let sealBusy = false;
  const enhanceSeal = () => {
    const container = form?.querySelector('.sealsubs-container');
    const v = currentVariant();
    if (!container || !v || sealBusy) return;
    sealBusy = true;
    form.classList.add('nc-seal');
    const target = container.parentElement;
    if (stepCount && valueEl?.dataset.purchaseLabel && !target.querySelector('.nc-seal-step')) {
      const step = document.createElement('div');
      step.className = 'nc-seal-step';
      step.innerHTML = `<span class="nc-o__step">${stepCount + 1}</span><span>${esc(valueEl.dataset.purchaseLabel)}</span>`;
      target.prepend(step);
    }
    const pct = sealPercent();
    container.querySelectorAll('.sls-option-container').forEach((box) => {
      const radio = box.querySelector('.sls-option');
      const labelBox = box.querySelector('.sls-label-container');
      if (!radio || !labelBox) return;
      box.classList.toggle('nc-seal-sub', radio.value === 'subscription');
      let extra = labelBox.querySelector('.nc-seal-extra');
      if (!extra) {
        extra = document.createElement('span');
        extra.className = 'nc-seal-extra';
        labelBox.appendChild(extra);
      }
      // Seal discounts recurring orders only, so the first charge stays at full price.
      if (radio.value === 'subscription' && pct) {
        const discounted = v.price * (1 - pct / 100);
        extra.innerHTML = `<span class="nc-seal-badge">Save ${pct}%</span><span class="nc-seal-price">${moneyRound(discounted)} <small>per recurring delivery</small></span>`;
      } else if (radio.value === 'one_time') {
        extra.innerHTML = `<span class="nc-seal-price">${money(v.price)}</span>`;
      }
      if (!box.dataset.ncClick) {
        box.dataset.ncClick = '1';
        box.addEventListener('click', (e) => {
          if (e.target.closest('select, .sls-description-button, a') || radio.checked) return;
          radio.click();
        });
      }
    });
    requestAnimationFrame(() => (sealBusy = false));
  };

  if (form) {
    let sealTimer;
    new MutationObserver(() => {
      if (sealBusy) return;
      clearTimeout(sealTimer);
      sealTimer = setTimeout(() => {
        enhanceSeal();
        updateBar();
      }, 60);
    }).observe(form, { childList: true, subtree: true });
    form.addEventListener('change', (e) => {
      if (e.target.matches('.sls-option, .sls-select')) updateBar();
    });
  }

  /* ---------- Sticky add to cart ---------- */
  const bar = document.querySelector('[data-nc-satc]');
  const mainButton = () => form?.querySelector('button[type="submit"], [name="add"]') || info.querySelector('[id^="ProductSubmitButton-"]');

  function updateBar() {
    if (!bar) return;
    const v = currentVariant();
    const btn = mainButton();
    const label = bar.querySelector('[data-nc-satc-label]');
    const add = bar.querySelector('[data-nc-satc-add]');
    if (v) {
      const sub = isSubscription();
      const pct = sealPercent();
      bar.querySelector('[data-nc-satc-price]').textContent = money(v.price);
      bar.querySelector('[data-nc-satc-variant]').textContent = v.options.filter((o) => o !== 'Default Title').join(' · ');
      bar.querySelector('[data-nc-satc-plan]').textContent = sub ? `${bar.dataset.labelSubscribe}${pct ? ` ${pct}% on repeats` : ''}` : bar.dataset.labelOnetime;
    }
    const disabled = !v || !v.available || btn?.hasAttribute('disabled');
    add.disabled = disabled;
    label.textContent = disabled ? bar.dataset.labelSoldout : bar.dataset.labelAdd;
  }

  if (bar && mainButton()) {
    const setVisible = (show) => {
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', String(!show));
      bar.querySelector('[data-nc-satc-add]').tabIndex = show ? 0 : -1;
      document.body.classList.toggle('nc-satc-on', show);
    };
    let target = mainButton();
    const io = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), { threshold: 0 });
    io.observe(target);

    bar.querySelector('[data-nc-satc-add]').addEventListener('click', () => {
      const btn = mainButton();
      if (!btn || btn.hasAttribute('disabled')) return;
      btn.click();
    });

    // The theme swaps form markup on variant change; keep observing the live button.
    const rebind = () => {
      const next = mainButton();
      if (next && next !== target) {
        io.unobserve(target);
        target = next;
        io.observe(target);
      }
    };
    window.addEventListener('scroll', rebind, { passive: true, once: true });
    updateBar();
  }

  /* ---------- Wiring ---------- */
  const refresh = () => {
    const v = currentVariant();
    updateValue(v);
    decorateOptions(v);
    enhanceSeal();
    updateBar();
  };

  refresh();

  // Style the theme's freshly fetched option markup *before* it is swapped in,
  // so the plain buttons never flash on variant change.
  if (typeof info.addPreProcessCallback === 'function') {
    info.addPreProcessCallback((incoming) => {
      const selects = incoming?.matches?.('variant-selects') ? incoming : incoming?.querySelector?.('variant-selects');
      if (!selects) return;
      const chosen = [...selects.querySelectorAll('[data-option-position]')]
        .sort((a, b) => a.dataset.optionPosition - b.dataset.optionPosition)
        .map((w) => w.querySelector('input[type="radio"]:checked')?.value);
      const next = findVariant(chosen);
      if (next) decorateOptions(next, selects);
    });
  }

  if (window.subscribe && window.theme?.PUB_SUB_EVENTS) {
    window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, () => {
      requestAnimationFrame(() => setTimeout(refresh, 30));
    });
  }
  idInput()?.addEventListener('change', () => setTimeout(refresh, 30));
})();
